import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Progress,
  Placeholder,
} from 'reactstrap';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import Phase1PartnersForm from './Phase1PartnersForm';
import Phase2CompanyForm from './Phase2CompanyForm';
import Phase3DocumentsForm from './Phase3DocumentsForm';
import Phase3CheckListForm from './Phase3CheckListForm';
import { companiesService } from '../../../services/companiesService';

type RegistrationType = 'portabilidade' | 'abertura';

interface WizardState {
  currentPhase: number;
  companyId: string | null;
  registrationType: RegistrationType;
  phase1Data: any;
  phase2Data: any;
  phase3Data: any;
  phase4Data: any;
  isSubmitting: boolean;
  isLoading: boolean;
  backendValidationError: string | null; // Track backend validation errors for field highlighting
}

const CompanyWizard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();

  // Get phase and type from query parameters
  const phaseParam = searchParams.get('phase');
  const typeParam = searchParams.get('type') as RegistrationType | null;
  const initialPhase = phaseParam ? parseInt(phaseParam, 10) : 1;
  const initialType: RegistrationType = typeParam || 'portabilidade';

  const [wizardState, setWizardState] = useState<WizardState>({
    currentPhase: initialPhase,
    companyId: id || null,
    registrationType: initialType,
    phase1Data: null,
    phase2Data: null,
    phase3Data: null,
    phase4Data: null,
    isSubmitting: false,
    isLoading: !!id, // Set to true if editing (has id)
    backendValidationError: null,
  });

  const loadCompanyData = useCallback(async (companyId: string) => {
    try {
      setWizardState((prev) => ({ ...prev, isLoading: true }));

      const company = await companiesService.getById(companyId);

      // Determine current phase: URL parameter takes absolute precedence
      // If user explicitly navigated to a phase via tab, respect that choice
      let currentPhase;
      if (phaseParam) {
        // URL parameter takes absolute precedence - user explicitly chose this phase
        currentPhase = parseInt(phaseParam, 10);
      } else {
        // Fallback to registration phase, but completed companies (phase 4) should default to phase 3
        let defaultPhase = company.registrationPhase || 1;
        if (defaultPhase === 4) {
          defaultPhase = 3; // Completed companies edit phase 3
        }
        currentPhase = defaultPhase;
      }

      // Map partners data (Phase 1)
      const phase1Data = company.partners && company.partners.length > 0 ? {
        partners: company.partners.map((p: any) => ({
          id: p.id, // Include partner ID for update operations
          name: p.name,
          cpf: p.cpf,
          phone: p.phone,
          senhaGov: p.senhaGov || '',
          dataNascimento: p.dataNascimento ? p.dataNascimento.split('T')[0] : '', // Convert ISO date to YYYY-MM-DD
          rg: p.rg || '',
          email: p.email || '',
          entidadeClasse: p.entidadeClasse || '',
          isActive: p.isActive !== undefined ? p.isActive : true,
          isMajoritario: p.isMajoritario !== undefined ? p.isMajoritario : false,
          address: p.address ? {
            cep: p.address.cep,
            street: p.address.street,
            number: p.address.number,
            complement: p.address.complement || '',
            neighborhood: p.address.neighborhood,
            city: p.address.city,
            state: p.address.state,
          } : null,
        })),
      } : null;

      // Map company data (Phase 2)
      // Populate if registration phase is >= 2 or if any Phase 2 data exists
      const hasPhase2Data = (company.registrationPhase && company.registrationPhase >= 2) || company.cnpj || company.taxRegime || company.cnaePrincipal;
      const phase2Data = hasPhase2Data ? {
        cnpj: company.cnpj || '',
        name: company.name || '',
        // Convert TaxRegime 0 (None) or null/undefined to empty string for dropdown
        taxRegime: (company.taxRegime && company.taxRegime !== 0) ? company.taxRegime : '',
        companyType: company.companyType || undefined,
        cnaePrincipal: company.cnaePrincipal || '',
        cnaeSecondary: company.cnaeSecondary || '',
        nire: company.nire || '',
        simplesCode: company.simplesCode || '',
        professionalEntity: company.professionalEntity || '',
        stateRegistration: company.stateRegistration || '',
        municipalRegistration: company.municipalRegistration || '',
        email: company.email || '',
        phone: company.phone || '',
        headquartersAddress: {
          street: company.hqStreet || '',
          number: company.hqNumber || '',
          complement: company.hqComplement || '',
          neighborhood: company.hqNeighborhood || '',
          city: company.hqCity || '',
          state: company.hqState || '',
          cep: company.hqCep || '',
        },
        invoicePassword: company.invoicePassword || '', // Load existing password if available
        invoiceEmissionPortalUrl: company.invoiceEmissionPortalUrl || '', // Load existing invoice emission portal URL if available
        porte: company.porte || '',
        capitalSocial: company.capitalSocial || undefined,
        dataAbertura: company.dataAbertura ? company.dataAbertura.split('T')[0] : '', // Convert ISO date to YYYY-MM-DD
        anexo: company.anexo || '',
        segmento: company.segmento || '',
        ramoAtividadeId: company.ramoAtividadeId || '',
        observacao: company.observacao || '',
        // Company ID, Number and Logo
        companyId: company.companyId,
        companyNumber: company.companyNumber || null,
        logoStorageId: company.logoStorageId || '',
        logoUrl: company.logoUrl || '',
      } : null;

      // Map certificates data (Phase 3)
      const phase3Data = company.certificate ? {
        hasEcpf: company.certificate.hasEcpf || false,
        hasEcnpj: company.certificate.hasEcnpj || false,
      } : null;

      // Determine registration type from company data
      const registrationType: RegistrationType =
        company.registrationType?.toLowerCase() === 'abertura' ? 'abertura' : 'portabilidade';

      setWizardState((prev) => ({
        ...prev,
        companyId,
        currentPhase,
        registrationType,
        phase1Data,
        phase2Data: registrationType === 'abertura' ? null : phase2Data, // For Portabilidade Phase 2
        phase3Data,
        phase4Data: registrationType === 'abertura' ? phase2Data : null, // For Abertura Phase 4 = Company Data
        isLoading: false,
      }));

      toast.success(t('Empresa carregada com sucesso'));
    } catch (error: any) {
      toast.error(error.message || t('Erro ao carregar empresa'));
      setWizardState((prev) => ({ ...prev, isLoading: false }));
      navigate('/apps-crm-companies');
    }
  }, [t, phaseParam, navigate]);

  // Load existing company data if editing
  useEffect(() => {
    if (id) {
      loadCompanyData(id);
    }
  }, [id, loadCompanyData]);

  const handlePhase1Submit = async (values: any) => {
    setWizardState((prev) => ({ ...prev, isSubmitting: true }));
    try {
      if (wizardState.companyId) {
        // Editing existing company - call updatePhase1 endpoint
        const result = await companiesService.updatePhase1(wizardState.companyId, values);

        setWizardState((prev) => ({
          ...prev,
          currentPhase: 2,
          phase1Data: {
            partners: result?.partners || values.partners, // Use partners with IDs from backend response
          },
          isSubmitting: false,
        }));

        toast.success(t('Sócios atualizados com sucesso!'));
      } else {
        // Creating new company - include registration type
        const result = await companiesService.createPhase1({
          ...values,
          registrationType: wizardState.registrationType === 'abertura' ? 'Abertura' : 'Portabilidade',
        });

        // Note: CheckList initialization is handled automatically by Phase3CheckListForm

        setWizardState((prev) => ({
          ...prev,
          currentPhase: 2,
          companyId: result.companyId,
          phase1Data: {
            partners: result.partners || values.partners, // Use partners with IDs from backend response
          },
          isSubmitting: false,
        }));

        toast.success(t('Fase 1 concluída com sucesso!'));
      }
    } catch (error: any) {
      // Extract error message from API response
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Message ||
        error.message ||
        t('Erro ao salvar sócios');

      toast.error(errorMessage);
      setWizardState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handlePhase2Submit = async (values: any) => {
    if (!wizardState.companyId) return;

    setWizardState((prev) => ({ ...prev, isSubmitting: true }));
    try {
      if (wizardState.registrationType === 'portabilidade') {
        // Portabilidade Phase 2: Company Data
        const response = await companiesService.updatePhase2(wizardState.companyId, values);

        setWizardState((prev) => ({
          ...prev,
          currentPhase: 3,
          phase2Data: {
            ...values,
            companyNumber: response.companyNumber,
          },
          isSubmitting: false,
        }));

        toast.success(t('Dados da empresa salvos com sucesso!'));
      } else {
        // Abertura Phase 2: Documents
        await companiesService.updatePhase3(wizardState.companyId, values);

        setWizardState((prev) => ({
          ...prev,
          currentPhase: 3,
          phase2Data: values, // Store documents data in phase2Data for Abertura
          isSubmitting: false,
        }));

        toast.success(t('Documentos salvos com sucesso!'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('Erro ao salvar'));
      setWizardState((prev) => ({
        ...prev,
        phase2Data: values,
        isSubmitting: false
      }));
    }
  };

  const handlePhase3Submit = async (values: any) => {
    if (!wizardState.companyId) return;

    setWizardState((prev) => ({ ...prev, isSubmitting: true }));
    try {
      if (wizardState.registrationType === 'portabilidade') {
        // Portabilidade Phase 3: Documents (final phase)
        await companiesService.updatePhase3(wizardState.companyId, values);

        const successMessage = id
          ? t('Empresa atualizada com sucesso!')
          : t('Empresa cadastrada com sucesso!');
        toast.success(successMessage);

        // Navigate to companies list after success
        setTimeout(() => {
          navigate('/apps-crm-companies');
        }, 1500);
      } else {
        // Abertura Phase 3: CheckList
        // CheckList data is auto-saved, just move to next phase
        setWizardState((prev) => ({
          ...prev,
          currentPhase: 4,
          phase3Data: values,
          isSubmitting: false,
        }));

        toast.success(t('CheckList salvo com sucesso!'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('Erro ao salvar'));
      setWizardState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handlePhase4Submit = async (values: any) => {
    if (!wizardState.companyId) return;

    setWizardState((prev) => ({ ...prev, isSubmitting: true, backendValidationError: null }));
    try {
      // Abertura Phase 4: Company Data (final phase)
      await companiesService.updatePhase2(wizardState.companyId, values);

      const successMessage = id
        ? t('Empresa atualizada com sucesso!')
        : t('Empresa cadastrada com sucesso!');
      toast.success(successMessage);

      // Navigate to companies list after success
      setTimeout(() => {
        navigate('/apps-crm-companies');
      }, 1500);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('Erro ao salvar dados da empresa');
      toast.error(errorMessage);
      // Store backend validation error for field highlighting
      setWizardState((prev) => ({
        ...prev,
        isSubmitting: false,
        backendValidationError: errorMessage
      }));
    }
  };

  const handlePreviousPhase = () => {
    setWizardState((prev) => ({
      ...prev,
      currentPhase: Math.max(1, prev.currentPhase - 1),
    }));
  };

  const getTotalPhases = (): number => {
    return wizardState.registrationType === 'abertura' ? 4 : 3;
  };

  const getProgressPercentage = (): number => {
    const totalPhases = getTotalPhases();
    const currentPhase = Math.min(wizardState.currentPhase, totalPhases);
    const progress = ((currentPhase - 1) / (totalPhases - 1)) * 100;
    return Math.min(progress, 100);
  };

  const isPhaseComplete = (phase: number): boolean => {
    switch (phase) {
      case 1:
        return wizardState.phase1Data !== null || wizardState.companyId !== null;
      case 2:
        return wizardState.phase2Data !== null;
      case 3:
        return wizardState.phase3Data !== null;
      case 4:
        return wizardState.phase4Data !== null;
      default:
        return false;
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={id ? t('Editar Empresa') : t('Nova Empresa')}
            pageTitle={t('CRM')}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="card-title mb-0">
                      {id ? t('Editar Empresa - Assistente') : t('Cadastro de Empresa - Assistente')}
                    </h4>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted">|</span>
                      <span className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                        {t('Progresso')}: {t(`Fase ${wizardState.currentPhase} de ${getTotalPhases()}`)} - {Math.round(getProgressPercentage())}%
                      </span>
                      <Progress
                        value={getProgressPercentage()}
                        color="success"
                        style={{ width: '150px', height: '8px' }}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  {/* Wizard Navigation Tabs */}
                  <Nav pills className="nav-pills-custom mb-4" role="tablist">
                    {/* Phase 1: Sócios (Both flows) */}
                    <NavItem>
                      <NavLink
                        className={classnames({
                          active: wizardState.currentPhase === 1,
                          done: isPhaseComplete(1),
                        })}
                        onClick={() => {
                          if (isPhaseComplete(1)) {
                            setWizardState((prev) => ({ ...prev, currentPhase: 1 }));
                            // Update URL to persist phase on refresh
                            const newUrl = `/apps-crm-companies/edit/${wizardState.companyId}?phase=1&type=${wizardState.registrationType}`;
                            window.history.replaceState(null, '', newUrl);
                          }
                        }}
                        style={{ cursor: isPhaseComplete(1) ? 'pointer' : 'not-allowed' }}
                      >
                        <span className="d-block d-sm-none">
                          <i className="ri-user-3-line"></i>
                        </span>
                        <span className="d-none d-sm-block">
                          {isPhaseComplete(1) && <i className="ri-check-line me-1"></i>}
                          {t('1. Sócios')}
                        </span>
                      </NavLink>
                    </NavItem>

                    {wizardState.registrationType === 'portabilidade' ? (
                      <>
                        {/* Portabilidade Phase 2: Company Data */}
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: wizardState.currentPhase === 2,
                              done: isPhaseComplete(2),
                            })}
                            onClick={() => {
                              if (isPhaseComplete(1)) {
                                setWizardState((prev) => ({ ...prev, currentPhase: 2 }));
                                // Update URL to persist phase on refresh
                                const newUrl = `/apps-crm-companies/edit/${wizardState.companyId}?phase=2&type=${wizardState.registrationType}`;
                                window.history.replaceState(null, '', newUrl);
                              }
                            }}
                            style={{ cursor: isPhaseComplete(1) ? 'pointer' : 'not-allowed' }}
                          >
                            <span className="d-block d-sm-none">
                              <i className="ri-building-line"></i>
                            </span>
                            <span className="d-none d-sm-block">
                              {isPhaseComplete(2) && <i className="ri-check-line me-1"></i>}
                              {t('2. Dados da Empresa')}
                            </span>
                          </NavLink>
                        </NavItem>

                        {/* Portabilidade Phase 3: Documents */}
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: wizardState.currentPhase === 3,
                              done: isPhaseComplete(3),
                            })}
                            onClick={() => {
                              if (isPhaseComplete(2)) {
                                setWizardState((prev) => ({ ...prev, currentPhase: 3 }));
                                // Update URL to persist phase on refresh
                                const newUrl = `/apps-crm-companies/edit/${wizardState.companyId}?phase=3&type=${wizardState.registrationType}`;
                                window.history.replaceState(null, '', newUrl);
                              }
                            }}
                            style={{ cursor: isPhaseComplete(2) ? 'pointer' : 'not-allowed' }}
                          >
                            <span className="d-block d-sm-none">
                              <i className="ri-file-text-line"></i>
                            </span>
                            <span className="d-none d-sm-block">
                              {isPhaseComplete(3) && <i className="ri-check-line me-1"></i>}
                              {t('3. Documentos')}
                            </span>
                          </NavLink>
                        </NavItem>
                      </>
                    ) : (
                      <>
                        {/* Abertura Phase 2: Documents */}
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: wizardState.currentPhase === 2,
                              done: isPhaseComplete(2),
                            })}
                            onClick={() => {
                              if (isPhaseComplete(1)) {
                                setWizardState((prev) => ({ ...prev, currentPhase: 2 }));
                                // Update URL to persist phase on refresh
                                const newUrl = `/apps-crm-companies/edit/${wizardState.companyId}?phase=2&type=${wizardState.registrationType}`;
                                window.history.replaceState(null, '', newUrl);
                              }
                            }}
                            style={{ cursor: isPhaseComplete(1) ? 'pointer' : 'not-allowed' }}
                          >
                            <span className="d-block d-sm-none">
                              <i className="ri-file-text-line"></i>
                            </span>
                            <span className="d-none d-sm-block">
                              {isPhaseComplete(2) && <i className="ri-check-line me-1"></i>}
                              {t('2. Documentos')}
                            </span>
                          </NavLink>
                        </NavItem>

                        {/* Abertura Phase 3: CheckList */}
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: wizardState.currentPhase === 3,
                              done: isPhaseComplete(3),
                            })}
                            onClick={() => {
                              if (isPhaseComplete(2)) {
                                setWizardState((prev) => ({ ...prev, currentPhase: 3 }));
                                // Update URL to persist phase on refresh
                                const newUrl = `/apps-crm-companies/edit/${wizardState.companyId}?phase=3&type=${wizardState.registrationType}`;
                                window.history.replaceState(null, '', newUrl);
                              }
                            }}
                            style={{ cursor: isPhaseComplete(2) ? 'pointer' : 'not-allowed' }}
                          >
                            <span className="d-block d-sm-none">
                              <i className="ri-task-line"></i>
                            </span>
                            <span className="d-none d-sm-block">
                              {isPhaseComplete(3) && <i className="ri-check-line me-1"></i>}
                              {t('3. Check List')}
                            </span>
                          </NavLink>
                        </NavItem>

                        {/* Abertura Phase 4: Company Data */}
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: wizardState.currentPhase === 4,
                              done: isPhaseComplete(4),
                            })}
                            onClick={() => {
                              if (isPhaseComplete(3)) {
                                setWizardState((prev) => ({ ...prev, currentPhase: 4 }));
                                // Update URL to persist phase on refresh
                                const newUrl = `/apps-crm-companies/edit/${wizardState.companyId}?phase=4&type=${wizardState.registrationType}`;
                                window.history.replaceState(null, '', newUrl);
                              }
                            }}
                            style={{ cursor: isPhaseComplete(3) ? 'pointer' : 'not-allowed' }}
                          >
                            <span className="d-block d-sm-none">
                              <i className="ri-building-line"></i>
                            </span>
                            <span className="d-none d-sm-block">
                              {isPhaseComplete(4) && <i className="ri-check-line me-1"></i>}
                              {t('4. Dados da Empresa')}
                            </span>
                          </NavLink>
                        </NavItem>
                      </>
                    )}
                  </Nav>

                  {/* Wizard Tab Content */}
                  {wizardState.isLoading ? (
                    <div className="py-4">
                      <Card className="mb-4">
                        <CardHeader>
                          <Placeholder animation="glow">
                            <Placeholder xs={6} />
                          </Placeholder>
                        </CardHeader>
                        <CardBody>
                          <Row>
                            <Col md={6} className="mb-3">
                              <Placeholder animation="glow">
                                <Placeholder xs={4} size="sm" className="mb-2" />
                                <Placeholder xs={12} />
                              </Placeholder>
                            </Col>
                            <Col md={6} className="mb-3">
                              <Placeholder animation="glow">
                                <Placeholder xs={4} size="sm" className="mb-2" />
                                <Placeholder xs={12} />
                              </Placeholder>
                            </Col>
                            <Col md={6} className="mb-3">
                              <Placeholder animation="glow">
                                <Placeholder xs={4} size="sm" className="mb-2" />
                                <Placeholder xs={12} />
                              </Placeholder>
                            </Col>
                            <Col md={6} className="mb-3">
                              <Placeholder animation="glow">
                                <Placeholder xs={4} size="sm" className="mb-2" />
                                <Placeholder xs={12} />
                              </Placeholder>
                            </Col>
                            <Col md={12} className="mb-3">
                              <Placeholder animation="glow">
                                <Placeholder xs={4} size="sm" className="mb-2" />
                                <Placeholder xs={12} />
                              </Placeholder>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                      <div className="d-flex justify-content-end">
                        <Placeholder animation="glow">
                          <Placeholder xs={2} className="btn btn-primary" />
                        </Placeholder>
                      </div>
                    </div>
                  ) : (
                    <TabContent activeTab={wizardState.currentPhase}>
                      {/* Phase 1: Sócios (Both flows) */}
                      <TabPane tabId={1}>
                        <Phase1PartnersForm
                          key={wizardState.companyId || 'new'}
                          onSubmit={handlePhase1Submit}
                          initialValues={wizardState.phase1Data}
                          isSubmitting={wizardState.isSubmitting}
                        />
                      </TabPane>

                      {wizardState.registrationType === 'portabilidade' ? (
                        <>
                          {/* Portabilidade Phase 2: Company Data */}
                          <TabPane tabId={2}>
                            <Phase2CompanyForm
                              key={wizardState.companyId || 'new'}
                              onSubmit={handlePhase2Submit}
                              onPrevious={handlePreviousPhase}
                              initialValues={wizardState.phase2Data}
                              isSubmitting={wizardState.isSubmitting}
                              companyId={wizardState.companyId || undefined}
                              isFinalPhase={false}
                            />
                          </TabPane>

                          {/* Portabilidade Phase 3: Documents */}
                          <TabPane tabId={3}>
                            <Phase3DocumentsForm
                              onSubmit={handlePhase3Submit}
                              onPrevious={handlePreviousPhase}
                              initialValues={wizardState.phase3Data}
                              isSubmitting={wizardState.isSubmitting}
                              companyId={wizardState.companyId || ''}
                              partners={wizardState.phase1Data?.partners || []}
                              isFinalPhase={true}
                              registrationType={wizardState.registrationType}
                            />
                          </TabPane>
                        </>
                      ) : (
                        <>
                          {/* Abertura Phase 2: Documents */}
                          <TabPane tabId={2}>
                            <Phase3DocumentsForm
                              onSubmit={handlePhase2Submit}
                              onPrevious={handlePreviousPhase}
                              initialValues={wizardState.phase2Data}
                              isSubmitting={wizardState.isSubmitting}
                              companyId={wizardState.companyId || ''}
                              partners={wizardState.phase1Data?.partners || []}
                              isFinalPhase={false}
                              registrationType={wizardState.registrationType}
                            />
                          </TabPane>

                          {/* Abertura Phase 3: CheckList */}
                          <TabPane tabId={3}>
                            <Phase3CheckListForm
                              onSubmit={handlePhase3Submit}
                              onPrevious={handlePreviousPhase}
                              companyId={wizardState.companyId || ''}
                              isSubmitting={wizardState.isSubmitting}
                              canReopenCompletedTasks={true}
                              isFinalPhase={false}
                            />
                          </TabPane>

                          {/* Abertura Phase 4: Company Data */}
                          <TabPane tabId={4}>
                            <Phase2CompanyForm
                              key={wizardState.companyId || 'new'}
                              onSubmit={handlePhase4Submit}
                              onPrevious={handlePreviousPhase}
                              initialValues={wizardState.phase4Data}
                              isSubmitting={wizardState.isSubmitting}
                              companyId={wizardState.companyId || undefined}
                              isFinalPhase={true}
                              backendValidationError={wizardState.backendValidationError}
                            />
                          </TabPane>
                        </>
                      )}
                    </TabContent>
                  )}

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-start mt-4">
                    <Button
                      color="secondary"
                      onClick={() => navigate('/apps-crm-companies')}
                    >
                      <i className="ri-arrow-left-line me-1"></i>
                      {t('Voltar')}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CompanyWizard;
